import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaPencilAlt,
  FaTrash,
  FaFilePdf,
} from "react-icons/fa";

import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import ConfirmDeleteModal from "../../components/confirmDeleteModal";
import LoadingOverlay from "../../components/loadingOverlay";
import { API_BASE } from "../../config/apiConfig";
import { useToast } from "../../hooks/useToast";

export default function CoachLeavesListing() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("access_token");
  const { showToast, ToastContainer } = useToast();

  // Backend data
  const [leaves, setLeaves] = useState([]);
  const [total, setTotal] = useState(0);

  // UI control
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Filters (default = current month/year)
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const limit = 8;
  const totalPages = Math.ceil(total / limit);

  // ======== FETCH OFF DAYS (FROM BACKEND) =========
  async function fetchOffDays(pageNum = 1) {
    try {
      setLoading(true);

      const url =
        `${API_BASE}/api/coach/leave/list?` +
        new URLSearchParams({
          page: pageNum,
          limit: limit,
          month: selectedMonth,
          year: selectedYear
        });

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (data.success) {
        setLeaves(data.items || []);
        setTotal(data.total || 0);
        setPage(pageNum);
      } else {
        showToast("Failed to load off days", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Error fetching off days", "error");
    } finally {
      setLoading(false);
    }
  }

  // Initial + re-fetch on month/year change
  useEffect(() => {
    fetchOffDays(1);
  }, [selectedMonth, selectedYear]);

  // ========= DELETE ==========
  async function handleDelete() {
    if (!deleteId) return;

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/api/coach/leave/delete/${deleteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) {
        showToast("Deleted successfully", "success");
        fetchOffDays(page); // reload current page
      } else {
        showToast("Delete failed", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Delete failed", "error");
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  }

  // ========= UTILITY: Format Timestamp → Date string ==========
  function formatDate(ts) {
    if (!ts) return "-";

    // Firestore Timestamp → JS Date
    if (typeof ts === "object" && ts.seconds) {
      return new Date(ts.seconds * 1000).toLocaleDateString();
    }

    // ISO string fallback
    return new Date(ts).toLocaleDateString();
  }

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear + i);

  async function handleExportPDF() {
    try {
      setLoading(true);

      const res = await fetch(
        `${API_BASE}/api/coach/leave/export?` +
          new URLSearchParams({
            month: selectedMonth,
            year: selectedYear,
          }),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `Coach_Leaves_${selectedYear}${
        selectedMonth === -1 ? "_All" : "_" + (selectedMonth + 1)
      }.pdf`;

      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      showToast("Failed to export PDF", "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <MainContentLayout>
      <PageHeader title="Coach Leaves" backTo="/dashboard" />

      {/* Filters */}
      <div className="flex my-4 justify-between">
        <div>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="border border-gray-400 rounded p-2 mr-1"
          >
            <option value={-1}>All Months</option>
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i} value={i}>
                {new Date(0, i).toLocaleString("default", { month: "long" })}
              </option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-gray-400 rounded p-2"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-center">
          <button
            onClick={handleExportPDF}
            className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700 mr-2"
            title="Export PDF"
          >
            <FaFilePdf size={20} />
          </button>
          {/* CREATE BUTTON */}
          <button
            onClick={() => navigate("/coach-leaves/new")}
            className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Apply Leave
          </button>
        </div>
      </div>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block">
        <table className="w-full border">
          <thead>
            <tr className="bg-gray-100 text-center">
              <th className="border px-3 py-2">#</th>
              <th className="border px-2 py-2">Dates</th>
              <th className="border px-3 py-2">Reason</th>
              <th className="border px-3 py-2">Actions</th>
            </tr>
          </thead>

          <tbody>
            {leaves.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center py-6 text-gray-500 italic">
                  No leaves found.
                </td>
              </tr>
            ) : (
              leaves.map((day, idx) => (
                <tr key={day.id}>
                  <td className="border px-2 py-2 text-center">
                    {(page - 1) * limit + idx + 1}
                  </td>

                  <td className="border px-2 py-2 text-center">
                    {formatDate(day.startDate)} — {formatDate(day.endDate)}
                  </td>

                  <td className="border px-2 py-2">
                    <div>{day.reasonLabel}</div>
                    {day.remark && (
                      <div className="text-sm text-gray-500 italic mt-1">
                        {day.remark}
                      </div>
                    )}
                  </td>

                  <td className="border px-2 py-2 text-center">
                    <button
                      onClick={() =>
                        navigate(`/coach-leaves/edit/${day.id}`)
                      }
                      className="text-yellow-600 hover:text-yellow-800 mr-2"
                    >
                      <FaPencilAlt />
                    </button>

                    <button
                      onClick={() => setDeleteId(day.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MOBILE CARD LIST */}
      <div className="md:hidden space-y-3">
        {leaves.length === 0 ? (
          <div className="text-center py-6 text-gray-500 italic">
            No leaves found.
          </div>
        ) : (
          leaves.map((day, idx) => (
            <div
              key={day.id}
              className="border rounded-lg p-3 shadow-sm text-sm bg-white"
            >
              <div className="flex justify-between">
                <div className="flex font-bold">
                  #{(page - 1) * limit + idx + 1}
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => navigate(`/coach-leaves/edit/${day.id}`)}
                    className="text-yellow-600 hover:text-yellow-800"
                  >
                    <FaPencilAlt />
                  </button>
                  <button
                    onClick={() => setDeleteId(day.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>

              <div className="mt-4 flex justify-between">
                <div className="font-bold">Dates:</div>
                <div>
                  {formatDate(day.startDate)} — {formatDate(day.endDate)}
                </div>
              </div>

              <div className="mt-4 flex justify-between">
                <div className="font-bold">Reason:</div>
                <div>
                  {day.reasonLabel}
                  {day.remark && (
                    <div className="text-gray-500 italic">{day.remark}</div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <button
            disabled={page === 1}
            onClick={() => fetchOffDays(page - 1)}
            className={`px-3 py-1 rounded ${
              page === 1
                ? "bg-gray-200 text-gray-400"
                : "bg-blue-600 text-white"
            }`}
          >
            Prev
          </button>

          <span>
            Page {page} / {totalPages}
          </span>

          <button
            disabled={page === totalPages}
            onClick={() => fetchOffDays(page + 1)}
            className={`px-3 py-1 rounded ${
              page === totalPages
                ? "bg-gray-200 text-gray-400"
                : "bg-blue-600 text-white"
            }`}
          >
            Next
          </button>
        </div>
      )}

      {/* Delete Modal */}
      {deleteId && (
        <ConfirmDeleteModal
          show={true}
          title="Delete Leave"
          message="Are you sure you want to delete this leave?"
          onCancel={() => setDeleteId(null)}
          onConfirm={handleDelete}
        />
      )}

      <ToastContainer />
      <LoadingOverlay show={loading} text="Processing..." />
    </MainContentLayout>
  );
}
